import React from "react";
import Loader from "../NewComponents/Loader";
import PortalSkeleton, { isPortalPath } from "../NewComponents/PortalSkeleton";
import { currentLang, translate } from "../i18n/checkout";
import { whenListShown } from "./listGate";

// Screens load as separate files, each the first time it is opened (see the
// route list in App.js), so the home page no longer downloads the office, the
// account area and the checkout before it can show anything.
//
// The price of that is the stale tab: after a deploy, a tab opened before it
// asks for files of the build it started with. Those normally stay on Hosting
// (tools/keep-previous-assets.js), but when one is missing the import fails
// with a ChunkLoadError. It is retried once (a dropped connection), and if it
// still fails the page reloads by itself, once per build, which brings the
// tab onto the current build. If even that does not help, ChunkErrorBoundary
// shows a short message with a Reload button instead of a blank page.

const RELOADED_KEY = "everbary.chunkReload";

export const isChunkLoadError = (error) =>
  !!error &&
  (error.name === "ChunkLoadError" ||
    error.code === "CSS_CHUNK_LOAD_FAILED" ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(error.message || ""));

// The build this tab runs, so a later deploy can earn its own reload.
const currentBuild = () => {
  const script = document.querySelector('script[src*="/static/js/main."]');
  return script ? script.getAttribute("src") : "dev";
};

// Reloads the page unless this tab already did for this build. Returns
// whether it did.
export const reloadOnceForNewBuild = () => {
  const build = currentBuild();
  try {
    if (window.sessionStorage.getItem(RELOADED_KEY) === build) {
      return false;
    }
    window.sessionStorage.setItem(RELOADED_KEY, build);
  } catch (e) {
    // No sessionStorage (privacy mode): never reload, to be sure not to loop.
    return false;
  }
  window.location.reload();
  return true;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// One retry for a dropped connection.
const withRetry = (factory) =>
  factory().catch((error) => {
    if (!isChunkLoadError(error)) throw error;
    return wait(1000).then(factory);
  });

// React.lazy with a `preload()` that starts the download ahead of the first
// render (it never reloads the page; only rendering the screen does). Both
// share one promise, so a preloaded screen renders without waiting again.
// `onRender` runs when the screen is first rendered.
export const lazyPage = (factory, { onRender } = {}) => {
  let promise = null;
  const preload = () => {
    if (!promise) {
      promise = withRetry(factory).catch((error) => {
        promise = null;
        throw error;
      });
    }
    return promise;
  };
  const Page = React.lazy(() => {
    if (onRender) onRender();
    return preload().catch((error) => {
      if (isChunkLoadError(error) && reloadOnceForNewBuild()) {
        // Keep the fallback on screen while the page reloads.
        return new Promise(() => {});
      }
      throw error;
    });
  });
  Page.preload = preload;
  return Page;
};

// Makes a React.lazy component render straight away the first time, without
// passing through its fallback. React.lazy only learns that its file is in
// when it first tries to render it, so even an already downloaded screen
// shows the fallback for a moment (a loader, an empty box). Priming runs that
// first attempt ahead of time; the promise settles once the component is
// ready (or failed: then rendering handles it as before).
export const primeLazy = (lazy) => {
  try {
    if (lazy && typeof lazy._init === "function") {
      lazy._init(lazy._payload);
    }
    return Promise.resolve();
  } catch (pending) {
    if (pending && typeof pending.then === "function") {
      return pending.then(() => {}, () => {});
    }
    return Promise.resolve();
  }
};

// The screens of one area (the office, the account, the checkout...). Each is
// its own file, so the first one opened downloads only what it needs; once one
// has been shown and the browser is idle, the rest of the area downloads in the
// background, so moving around the area does not wait again. A screen with
// `background: false` is left out of that (it only loads when opened).
export const lazyGroup = ({ onRender } = {}) => {
  const members = [];
  let warmed = false;
  const warmSiblings = () => {
    if (warmed) return;
    warmed = true;
    const run = () => members.forEach(({ page, background }) => background && page.preload().catch(() => {}));
    // Not before the screen's list has its rows (listGate): on a slow line
    // these downloads would share it with the list's data.
    setTimeout(() => whenListShown(() => {
      setTimeout(() => (window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 5000 }) : run()), 1000);
    }), 1000);
  };
  return (factory, { background = true, onRender: onRenderThis } = {}) => {
    const first = onRenderThis || onRender;
    const page = lazyPage(factory, {
      // Only a screen actually shown warms its area (not the startup preload,
      // which may be for someone about to be sent elsewhere).
      onRender: () => {
        if (first) first();
        page.preload().then(warmSiblings, () => {});
      },
    });
    members.push({ page, background });
    return page;
  };
};

// Shown while a screen's file downloads. Same as the app's own sign-in wait,
// so the two read as one: an account or office page's layout in grey
// (PortalSkeleton), a spinner elsewhere.
export const PageFallback = () => (isPortalPath(window.location.pathname) ? <PortalSkeleton /> : (
  <div className="container mx-auto w-full h-dvh flex items-center justify-center">
    <Loader />
  </div>
));

// Catches only a screen that could not be downloaded. Any other error is
// passed on untouched, so the app behaves exactly as before for those.
export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    // In the language of the last page drawn (Spanish on the Spanish checkout, and so on).
    const lang = currentLang();
    return (
      <div className="container mx-auto w-full h-dvh flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-lg">{translate(lang, "chunk.failed")}</p>
        <button
          type="button"
          className="rounded-full px-6 py-2 text-white"
          style={{ backgroundColor: "#18b7bb" }}
          onClick={() => window.location.reload()}
        >
          {translate(lang, "chunk.reload")}
        </button>
      </div>
    );
  }
}
